import React, { useMemo, forwardRef, useState, useEffect } from "react";
import { Spin, Collapse, Typography, Tooltip, List, Space, Col, Row, Divider, Badge, Tag } from "antd";
import classnames from "classnames";
import _, { isEmpty } from "lodash";
import { CellRenderers } from "utils/tableUtils";
import { getSeverityByBaseScore } from "utils/riskScoreUtil";
import { SEVERITY_COLOR } from "constants/Layout";
import BaseModal from 'components/BaseModal';
import { LoadingIcon, LinkIcon, TrendingIcon, CircleCheckIcon, ExpolitedIconV2 } from 'components/Icons';
import { CollapseHeaderDivider } from 'views/Findings/FindingDetailsModalV2';
import { MODAL_SIZE_EXTRA_LARGE } from 'constants/Layout';
import FeedsAPI from './FeedsAPI';
import { TableContainer } from "components/TableContainer";
import { ThreatTag } from "components/ThreatsTag";
import { FindingDetailsGrid } from "views/Findings/FindingDetails/FindingDetailsGrid";
import EPSSScore from "views/Findings/EPSSScore";
import ACSettingSectionTitle from "ac-components/ac-setting-section-title/ACSettingSectionTitle";
import ACSettingSectionCard from "ac-components/ac-setting-section-card/ACSettingSectionCard";
import { useFeatureFlag } from "contexts/FeatureFlagContext";


const exploitTagsFormatter = (data) =>{
  const dataLowerCase = _.toLower(data)
  return dataLowerCase === "poc" || dataLowerCase === "proof of concept" || dataLowerCase === "in the wild" || dataLowerCase === "pentesterframework"
}

export default forwardRef(function FeedDetailsModal({ feedId, afterClose }, ref) {
    const [data, setData] = useState();
    const featureFlags = useFeatureFlag();
    useEffect(() => {
        const { promise, cancel } = FeedsAPI.getFeedDetails('CSW', feedId);
        promise.then(setData);
        return cancel;
    }, [feedId]);

    const CVERedirectLink = `${window.location.origin}/compliance/vulnerability-rules/${feedId}/redirect`;
    const isTrending = _.get(data, 'otherProperties.isTrending');

    const title = (
        <div className='d-flex align-items-center justify-content-between'>
            <div>
                <span className='clickable' onClick={() => window.open(CVERedirectLink, '_blank', 'noopener noreferrer')}><u>{feedId}</u></span>
                <Typography.Text copyable={{
                    tooltips: ["", ""],
                    text: CVERedirectLink,
                    icon: [
                        <Tooltip title="Copy link for CVE Detail" placement='bottom'><LinkIcon key="copy-icon" className="font-size-md" style={{ color: "black" }} /></Tooltip>, 
                        <Tooltip title="Copied" placement='bottom'><CircleCheckIcon key="copied" className="font-size-md" /></Tooltip>
                    ],
                }} />
            </div>
            <div>
                {
                    _.isUndefined(data) ?
                        <LoadingIcon />
                        :
                        <>
                            <ScoreBox tooltip='AATI Score' score={_.get(data, "aatiScore")} />
                            <ScoreBox tooltip='Base Score' score={_.get(data, 'otherProperties.baseScore')} border />
                            {isTrending && <RenderTrendingIcon />}
                            {validateExploitedIconCondition(_.get(data, 'otherProperties.prioritizedBy')) && <RenderExploitedIcon className={classnames({ 'm-l-xs': isTrending })} />}
                        </>
                }
            </div>
        </div>
    )
    
    return (
        <BaseModal
            ref={ref}
            afterClose={afterClose}
            modalProps={{
                width: MODAL_SIZE_EXTRA_LARGE, 
                className: "modal-v2 modal-full-height no-footer feed-details",
                title,
                footer: null
            }}
        >
            <Spin spinning={_.isUndefined(data)}>
                <Typography.Paragraph>{_.get(data, 'description')}</Typography.Paragraph>
                <div className='m-b-l' />
                {!isEmpty(data?.otherProperties?.AATI_SCORE_COMMENTARIES) && featureFlags.AATIV2_SCORE && <div className="m-b-l">
                            <ACSettingSectionTitle
                              title="Details"
                              description="How This AATI Score was established and its potential impact"
                            />
                            <ACSettingSectionCard contentPadding={true}>
                              {_.map(data.otherProperties.AATI_SCORE_COMMENTARIES, (commentary) => {
                                return <ul>
                                  <li>{commentary}</li>
                                </ul>
                              })}
                            </ACSettingSectionCard>
                          </div>}
                {data?.threatTags ? <div className="d-flex align-items-center">
                  {_.get(data.threatTags, "proof of concept") && <ThreatTag title={"Proof Of Concept"} count={_.get(data.threatTags, "proof of concept")} className={"m-r-sm"}/>}
                  {_.get(data.threatTags, "in the wild") && <ThreatTag title={"In The Wild"} count={_.get(data.threatTags, "in the wild")} className={"m-r-sm"}/>}
                  {_.get(data.threatTags, "pentester framework") && <ThreatTag title={"Pentester Framework"} count={_.get(data.threatTags, "pentester framework")} className={"m-r-sm"}/>}
                  {_.get(data.threatTags, "code snippet") && <ThreatTag title={"Code Snippet"} count={_.get(data.threatTags, "code snippet") ? "Y" : "N"} className={"m-r-sm"}/>}
                  {data.otherProperties?.epssProbability && (
                    <span style={{ fontWeight: 800, marginRight: "10px" }}>
                      EPSS Score: <EPSSScore epssScore={data.otherProperties?.epssProbability} />
                    </span>
                  )}
                  {data.epssScore && (
                    <span style={{ fontWeight: 800 }}>
                      EPSS Percentile: <EPSSScore epssScore={data.epssScore} />
                    </span>
                  )}
                </div> : null}
                <div className='m-b-l' />
                <Section1 data={data} />
                <div className='m-b-l' />
                <Section2 data={_.get(data, 'availabilityOfPatch') || {}} />
                <div className='m-b-l' />
                <Section3 data={data}/>
            </Spin>
        </BaseModal>
    );
})

export function ScoreBox({ score, tooltip, border }) {
    const scoreToDisplay = CellRenderers.VALUE_OR_NA(score);
    const style = useMemo(() => {
        const _style = {
            width: 51,
        };
        if (scoreToDisplay === CellRenderers.VALUE_OR_NA()) {
            return _style;
        }
        const color = SEVERITY_COLOR[getSeverityByBaseScore(+score)];
        _style.border = color;
        if (!border) {
            _style.background = color;
            _style.color = '#fff';
        } else {
            _style.color = '#000';
            _style.border = `1px solid ${color}`;
        }
        return _style;
    }, [score, border, scoreToDisplay]);

    return (
        <Tooltip title={tooltip} placement='bottom'>
            <span className='d-inline-block text-center border m-r-xs' style={style}>
                {scoreToDisplay}
            </span>
        </Tooltip>
    );
}

// const validateTrendingIconCondition = function(lastTrendingDate) {
//     return lastTrendingDate && moment().diff(moment(lastTrendingDate), 'd') <= 7;
// };

const validateExploitedIconCondition = function(value) {
    return _.includes((value || []), 'CISA');
};

export function RenderTrendingIcon({ className }) {
    return (
        <Tooltip title='Trending' placement='bottom'>
            <TrendingIcon className={classnames('font-size-lg', className)} />
        </Tooltip>
    );
}

// RenderTrendingIcon.validateCondition = validateTrendingIconCondition;

export function RenderExploitedIcon({ className }) {
    return  (
        <Tooltip title='Exploit Available' placement='bottom'>
            <ExpolitedIconV2 className={classnames('font-size-lg', className)} />
        </Tooltip>
    );
}

RenderExploitedIcon.validateCondition = validateExploitedIconCondition;

const Indicator = ({color})=>{
    return <div style={{backgroundColor: color, height: "10px",width: "10px",borderRadius: "50%"}} />
}

const TitlePill = ({borderColor, count, title}) => {
   return <div className='d-flex align-items-center title-pill' style={{border:`1px solid ${borderColor}`, color:`${borderColor}`, opacity: count > 0 ? "1" : "0.8"}}>
        <span className="m-0 title-pil-p-r">{title}</span>
        <span style={{borderLeft:`1px solid ${borderColor}`, color:"#000"}} className="m-0 title-pil-p-l h-100">{count}</span>
    </div>
}

function Section1({ data }) {
    const exploitsList = useMemo(() => [
        { label: "Attack Classification", path: "attackClassifications", valueClassName: 'break-content', span: 24, formatter:  (value) => value.join(', ')},
        { label: "Date published", path: "publishedDate", valueClassName: 'break-content', span: 24 },
        { label: "Last Modified", path: "lastModifiedDate", valueClassName: 'break-content', span: 24 },
        {
          label: "Sources Name",
          path: "sources",
          valueClassName: 'break-content',
          span: 24,
          formatter: (entries) => (
              entries.map((source, index) => (
                <>
                  <a href={source.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline', color: 'inherit' }}>
                      {source.name}
                  </a>
                  {index < entries.length - 1 && ', '}
                </>
              ))
          )
      },
        { label: "Tags", path: "tags", valueClassName: 'break-content', span: 24, formatter:  (entries) => _.filter(entries, exploitTagsFormatter).join(', ')},
    ], []);

    // const ransomwareList = useMemo(() => [
    //     { label: "Aliases", path: "aliases", valueClassName: 'break-content', span: 24, formatter:  (value) => value.join(', ')},
    //     { label: "Family", path: "family", valueClassName: 'break-content', span: 24 },
    // ], []);

    const malwareList = useMemo(() => [
        { label: "Aliases", path: "aliases", valueClassName: 'break-content', span: 24, formatter:  (value) => value.join(', ')},
        { label: "Family", path: "family", valueClassName: 'break-content', span: 24 },
        {
          label: "Sources Name",
          path: "sources",
          valueClassName: 'break-content',
          span: 24,
          formatter: (entries) => (
              entries.map((source, index) => (
                <>
                  <a href={source.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline', color: 'inherit' }}>
                      {source.name}
                  </a>
                  {index < entries.length - 1 && ', '}
                </>
              ))
          )
      },
    ], []);

    const threatActorList = useMemo(() => [
        { label: "Associated Groups", path: "associatedGroups", valueClassName: 'break-content', span: 24, formatter:  (value) => value.join(', ')},
    ], []);
    const exploits = _.get(data, 'exploits') || [];
    // const ransomewares = _.get(data, 'ransomewares') || [];
    const malwares = _.get(data, 'malwares') || [];
    const threatActors = _.get(data, 'threatActors') || [];

    const exploitTitle = <TitlePill borderColor="#E05252" count={exploits.length} title="EXPLOITS"/>
    // `EXPLOITS (${exploits.length})`;
    // const ransomewareTitle = `RANSOMWARES (${ransomewares.length})`;
    const malwareTitle = <TitlePill borderColor="#E05252" count={malwares.length} title="MALWARES"/>
    // `MALWARES (${malwares.length})`
    const threatActorTitle = <TitlePill borderColor="#E05252" count={threatActors.length} title="THREAT ACTORS"/>
    // `THREAT ACTORS (${threatActors.length})`;

    const header = (
        <div className='d-flex align-items-center'>
            {exploitTitle}
            <CollapseHeaderDivider />
            {malwareTitle}
            <CollapseHeaderDivider />
            {threatActorTitle}
        </div>
    );

    return (
        <Collapse className='collapse-v2' defaultActiveKey="section1">
            <Collapse.Panel header={header} key="section1" className="detail-section">
              <Section1.SubSection title={<Space align="center"><Indicator color={"#E05252"}/>EXPLOITS</Space>} list={exploitsList} labelWidth={170} data={exploits} />
              <div className='m-b-l'/>
              {/* <Section1.SubSection title={ransomewareTitle} list={ransomwareList} labelWidth={70} data={ransomewares} /> */}
              <Section1.SubSection title={<Space align="center"><Indicator color={"#E05252"}/>MALWARES</Space>} list={malwareList} labelWidth={105} data={malwares} />
              <div className='m-b-l'/>
              <Section1.SubSection title={<Space align="center"><Indicator color={"#E05252"}/>THREAT ACTORS</Space>} list={threatActorList} labelWidth={105} data={threatActors} />
            </Collapse.Panel>
        </Collapse>
    );
}

Section1.SubSection = function({ title, data, list, labelWidth }) {
    return (
        <section>
            <Typography.Paragraph strong>{title}</Typography.Paragraph>
            <div className='m-l-l'>
            {
                _.isEmpty(data) ?
                    CellRenderers.VALUE_OR_NA()
                    :
                    _.map(data, (o) => (
                        <div>
                            <Typography.Paragraph strong className='m-0'>{o.title || o.name}</Typography.Paragraph>
                            <div className='m-l-l m-y-xs'>
                                <FindingDetailsGrid data={o} list={list} labelWidth={labelWidth} gutter={[0, 0]}/>
                            </div>
                        </div>
                    ))
            }
            </div>
        </section>
    );
};

function Section2({ data }) {
    const workArounds = _.get(data, 'workArounds') || [];
    const patch = _.get(data, 'patch') || [];
    const patchUpgrades = _.get(data, 'patchUpgrades') || [];

    const header = (
        <div className='d-flex align-items-center'>
            <TitlePill count={workArounds.length} title="WORKAROUNDS" borderColor={"#50C988"}/>
            <CollapseHeaderDivider />
            <TitlePill count={patch.length} title="PATCH" borderColor={"#50C988"}/>
            <CollapseHeaderDivider />
            <TitlePill count={patchUpgrades.length} title="PATCH UPGRADES" borderColor={"#50C988"}/>
        </div>
    );
    return (
        <Collapse className='collapse-v2'>
            <Collapse.Panel header={header} key="section2" className="detail-section">
                {
                    _.map(['workArounds', 'patch', 'patchUpgrades'], (key) => (
                       <>
                            <Typography.Paragraph strong className='m-0'>{<Space size={"small"}><Indicator color={"#63BE3F"}/> {_.upperCase(key)}</Space>}</Typography.Paragraph>
                            {
                                _.isEmpty(data[key]) ?
                                    CellRenderers.VALUE_OR_NA()
                                    :
                                    _.map(data[key], (link) => <div className="m-l-xs"><Typography.Link href={link} className="m-l-l">{link}</Typography.Link></div>)
                            }
                            <div className='m-b-l'/>
                        </>
                    ))
                }
            </Collapse.Panel>
        </Collapse>
    )
}

function Section3({ data }) {
    const { otherProperties } = data || {};
    const postCount = _.get(otherProperties, "posts") || [];
    const affectedSoftwareCount = _.get(otherProperties, "affectedSoftwareConfigurations.affectedProductCount") || 0;
    const pluginsCount = _.get(otherProperties,"plugins") || [];
    const refCount = _.get(otherProperties,"refs")|| [];
    const header = (
        <div className='d-flex align-items-center'>
            HACKER DISCUSSION ({postCount.length})
            <CollapseHeaderDivider />
            AFFECTED PLATFORMS ({affectedSoftwareCount})
            <CollapseHeaderDivider />
            ASSOCIATED PLUGINS ({pluginsCount.length})
            <CollapseHeaderDivider />
            REFERENCES ({refCount.length})
            {/* <CollapseHeaderDivider />
            EXPLOITS IN THE WILD (0) */}
        </div>
    );
    return (
        <Collapse className='collapse-v2'>
            <Collapse.Panel header={header} key="section2" className="detail-section">
                {
                    <>
                       <Space><><Indicator color={"#35198E"}/><Typography.Paragraph strong className='m-0'>HACKER DISCUSSION</Typography.Paragraph></></Space>
                        <div className='m-b-xs'/>
                        <HackerDiscussion data={otherProperties}/>
                        <div className='m-b-l'/>
                        <Space><><Indicator color={"#35198E"}/><Typography.Paragraph strong className='m-0'>AFFECTED PLATFORMS</Typography.Paragraph></></Space>
                        <div className='m-b-xs'/>
                        <AffectedPlatforms data={otherProperties}/>
                        <div className='m-b-l'/>
                        <Space><><Indicator color={"#35198E"}/><Typography.Paragraph strong className='m-0'>ASSOCIATED PLUGINS</Typography.Paragraph></></Space>
                        <div className='m-b-xs'/>
                        <AssociatedPlugins data={otherProperties}/>
                        <div className='m-b-l'/>
                        <Space><><Indicator color={"#35198E"}/><Typography.Paragraph strong className='m-0'>REFERENCES</Typography.Paragraph></></Space>
                        <div className='m-b-xs'/>
                        <References data={otherProperties}/>
                    </>
                }
            </Collapse.Panel>
        </Collapse>
    )
}

function HackerDiscussion({ data }) {
    const { posts } = data || {};
    const { Paragraph, Text } = Typography;
    let content = {};
    if (!_.isEmpty(posts)) {
      posts.forEach((post) => {
        let { publishedDate, source } = post;
        let dateFormatted;
        let sourceUrl = "";
        let sourceName = "";
        publishedDate = new Date(publishedDate);
        let publishedYear = publishedDate.getFullYear();
        dateFormatted = publishedDate.toTimeString();
  
        //as per the mockup only show time with GMT removing extra Timezone info
        const extras = dateFormatted.indexOf("(");
        dateFormatted = dateFormatted.slice(0, extras).trim();
        let publishedMonth = publishedDate.toLocaleString("default", {
          month: "short",
          day: "numeric",
        });
        publishedDate = { date: publishedMonth, time: dateFormatted };

        //as per the mockup, display url removing protocol and www
        if (!_.isNull(source.url)) {
          sourceUrl = new URL(source.url);
          sourceName = sourceUrl.hostname.replace(/^www\./, "");
          sourceName = sourceName.replace(/^https?:\/\//i, "").replace(/\//g, "");
          source.url = sourceName;
        }
        post = { ...post, publishedDate, sourceName };
        if (!_.isEmpty(content[publishedYear])) {
          content[publishedYear].push(post);
        } else {
          content[publishedYear] = [post];
        }
      });
  
      return Object.keys(content).map((key) => {
        return (
          <div className="m-l-l hacker-discussion">
            <Text strong>
              {key}
            </Text>
            <List
              itemLayout="horizontal"
              dataSource={content[key]}
              renderItem={(item) => (
                <List.Item>
                  <List.Item.Meta
                    avatar={
                      <Badge color={"#35198E"}/>
                    }
                    title={
                      <Space size={"large"} className="d-flex align-items-center">
                        <Text strong>{item.publishedDate.date}</Text>
                        <Text type="secondary">
                          {item.publishedDate.time}
                        </Text>
                        <Text strong>
                          Type: {CellRenderers.VALUE_OR_NA(item.type)}
                        </Text>
                        <Text strong>
                          Website: {CellRenderers.VALUE_OR_NA(item.sourceName)}
                        </Text>
                      </Space>
                    }
                    description={
                      <Paragraph ellipsis={{ rows: 3, expandable: true }}>
                        {CellRenderers.VALUE_OR_NA(item.content)}
                      </Paragraph>
                    }
                  />
                </List.Item>
              )}
            />
          </div>
        );
      });
    }
    return CellRenderers.VALUE_OR_NA();
  }
  
  function AffectedPlatforms({ data }) {
    const { affectedSoftwareConfigurations } = data || {};
    const { softwareConfigurations } = affectedSoftwareConfigurations || {};
    const columns = useMemo(
      () => [
        {
          title: "CPE",
          dataIndex: "cpe23Uri",
          render: CellRenderers.VALUE_OR_NA,
        },
        {
          title: "Vendor",
          dataIndex: "vendor",
          render: CellRenderers.VALUE_OR_NA,
        },
        {
          title: "Product",
          dataIndex: "product",
          render: CellRenderers.VALUE_OR_NA,
        },
        {
          title: "Version",
          dataIndex:"version",
          render: CellRenderers.VALUE_OR_NA,
        },
      ],
      []
    );
  
    return !_.isEmpty(affectedSoftwareConfigurations) ? (
      <div className="m-l-l"><TableContainer columns={columns} dataSource={softwareConfigurations} /></div>
    ) : (
      CellRenderers.VALUE_OR_NA()
    );
  }
  
  function AssociatedPlugins({ data }) {
    const { plugins } = data || {};
    let contents = [];
    if (!_.isEmpty(plugins)) {
      contents = plugins.map((plugin) => {
        let { publishedDate, lastModifiedDate } = plugin;
        if (publishedDate) {
          publishedDate = new Date(publishedDate).toLocaleDateString(undefined, {
            day: "2-digit",
            month: "short",
            year: "numeric",
          });
        }
        if (lastModifiedDate) {
          lastModifiedDate = new Date(lastModifiedDate).toLocaleDateString(
            undefined,
            { day: "2-digit", month: "short", year: "numeric" }
          );
        }
  
        return { ...plugin, publishedDate, lastModifiedDate };
      });
      return (
        <div className="m-l-l">
          <Row>
            {contents.map((content) => {
              const {
                id,
                category,
                description,
                lastModifiedDate,
                publishedDate,
                scannerName,
                title,
                severity,
              } = content;
  
              return (
                <>
                  <Col span={24}>
                    <Space>
                      <Typography.Text strong className="m-b-xs">
                        Plugin ID-{id}
                      </Typography.Text>
                      <Divider type="vertical" />
                      <Typography.Text strong className="m-b-xs">
                        Scanner Name-{scannerName}
                      </Typography.Text>
                    </Space>
                  </Col>
                  <Col span={6}>Severity:</Col>
                  <Col span={18}>{CellRenderers.VALUE_OR_NA(severity)}</Col>
                  <Col span={6}>Title:</Col>
                  <Col span={18}>{title}</Col>
                  <Col span={6}>Description:</Col>
                  <Col span={18}>
                    <Typography.Paragraph
                      ellipsis={{ rows: 3, expandable: true }}
                    >
                      {CellRenderers.VALUE_OR_NA(description)}
                    </Typography.Paragraph>
                  </Col>
                  <Col span={6}>Category:</Col>
                  <Col span={18}>{CellRenderers.VALUE_OR_NA(category)}</Col>
                  <Col span={6}>Published Date:</Col>
                  <Col span={18}>{CellRenderers.VALUE_OR_NA(publishedDate)}</Col>
                  <Col span={6}>Updated Date:</Col>
                  <Col span={18}>
                    <div className="m-b-l">{CellRenderers.VALUE_OR_NA(lastModifiedDate)}</div>
                  </Col>
                </>
              );
            })}
          </Row>
        </div>
      );
    }
    return <>{CellRenderers.VALUE_OR_NA()}</>;
  }
  
  function References({ data }) {
    const { refs } = data || {};
  
    const Tags = ({ tags }) => {
      return !_.isEmpty(tags) ? 
        CellRenderers.PLUS_MORE_SINGLE_LINE({values: tags, className: 'text-ellipsis', style: { maxWidth: 200 }, Component: Tag, limit: 2, delimiter: ''})
       : 
        CellRenderers.VALUE_OR_NA();
    };

    if (!_.isEmpty(refs)) {
      return refs.map((ref) => {
        const { tags, url } = ref;
        return (
          <div>
          <Space className="m-l-l" size={"large"} align="start">
            <Typography.Link href={url}>{url}</Typography.Link>
            <Tags tags={tags} />
          </Space>
          </div>
        );
      });
    }
  
    return CellRenderers.VALUE_OR_NA();
  }


